/**
 * /blogs/[blog]/tagged/[tag]?display=compact
 *
 * Filters and pagination for the blog index.
 *
 * Clicking a tag or "Load more" asks Shopify for this section's HTML at the
 * new URL, and swaps the article list, so the rest of the page stays put.
 *
 * Display mode (grid or compact) lives on the URL as "display"—Shopify already
 * uses "view" for templates—so back/forward keep the choice, but a fresh visit
 * always uses the section default.
 *
 * "Load more" sends ?page=N, but does not add that to the address bar.
 *
 */

(() => {
  const DISPLAY_PARAM = "display";
  const VIEWS = new Set(["grid", "compact"]);

  const parseView = (value) => (VIEWS.has(value) ? value : "");

  const withPath = (url) => {
    const next = new URL(window.location.href);
    const target = new URL(url, window.location.origin);
    next.pathname = target.pathname;
    const page = target.searchParams.get("page");
    if (page) next.searchParams.set("page", page);
    else next.searchParams.delete("page");
    next.searchParams.delete("section_id");
    next.searchParams.delete("sections");
    return next;
  };

  const publicUrl = (url, { view, defaultView }) => {
    const next = withPath(url);
    if (view && view !== defaultView) {
      next.searchParams.set(DISPLAY_PARAM, view);
    } else {
      next.searchParams.delete(DISPLAY_PARAM);
    }
    return `${next.pathname}${next.search}${next.hash}`;
  };

  const sectionRequestUrl = (url, sectionId) => {
    const next = withPath(url);
    next.searchParams.delete(DISPLAY_PARAM);
    next.searchParams.set("section_id", sectionId);
    return next.toString();
  };

  class BlogIndex extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const { dataset } = this;
      this.defaultView = parseView(dataset.defaultView) || "grid";
      this.sectionId = dataset.sectionId || "";
      this.blogUrl = dataset.blogUrl || "";
      this.currentTag = dataset.currentTag || "";
      this.listing = this.querySelector("[data-blog-listing]");
      this.categoryMenu = this.querySelector("[data-blog-category]");
      this.categoryLabel = this.querySelector("[data-blog-category-label]");
      this.loading = false;
      this.abort = null;
      this.view = this.readView();
      this.setView(this.view);
      this.syncTags();
      this.syncUrl({ replace: true });

      this.onClick = this.handleClick.bind(this);
      this.onPopState = this.handlePopState.bind(this);
      this.addEventListener("click", this.onClick);
      window.addEventListener("popstate", this.onPopState);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.removeEventListener("click", this.onClick);
      window.removeEventListener("popstate", this.onPopState);
    }

    readView() {
      const fromUrl = parseView(
        new URL(window.location.href).searchParams.get(DISPLAY_PARAM),
      );
      return fromUrl || this.defaultView;
    }

    setView(view) {
      this.view = view;
      this.dataset.view = view;
      this.querySelectorAll("[data-view-when]").forEach((el) => {
        el.hidden = el.dataset.viewWhen !== view;
      });
    }

    syncTags() {
      this.querySelectorAll("[data-blog-filter][data-tag]").forEach((el) => {
        const active = el.dataset.tag === this.currentTag;
        el.setAttribute("aria-pressed", String(active));
        el.classList.toggle("bg-black", active);
        el.classList.toggle("text-white", active);
        el.classList.toggle("bg-off-white", !active);
        el.classList.toggle("text-inherit", !active);
      });
      if (this.categoryLabel) {
        this.categoryLabel.textContent =
          this.currentTag || this.categoryLabel.dataset.placeholder || "";
      }
    }

    handleClick(event) {
      const toggle = event.target.closest("[data-view-toggle]");
      if (toggle) {
        event.preventDefault();
        this.toggleView();
        return;
      }

      const more = event.target.closest("[data-blog-more]");
      if (more) {
        const link = more.tagName === "A" ? more : more.querySelector("a");
        if (!link?.href) return;
        event.preventDefault();
        this.loadMore(link.href);
        return;
      }

      const filter = event.target.closest("[data-blog-filter]");
      if (!filter) return;
      event.preventDefault();
      this.selectTag(filter.dataset.tag || "", filter.dataset.taggedUrl || filter.href);
    }

    toggleView() {
      this.setView(this.view === "grid" ? "compact" : "grid");
      this.syncUrl({ replace: true });
    }

    closeCategoryMenu() {
      if (this.categoryMenu) this.categoryMenu.open = false;
    }

    tagFromPath(pathname) {
      const match = pathname.match(/\/tagged\/([^/]+)\/?$/);
      if (!match) return "";
      const handle = decodeURIComponent(match[1]);
      const link = this.querySelector(
        `[data-tag-handle="${CSS.escape(handle)}"]`,
      );
      return link?.dataset.tag || "";
    }

    filterUrl(tag, taggedUrl) {
      if (!tag || this.currentTag === tag) return this.blogUrl;
      return taggedUrl;
    }

    async selectTag(tag, taggedUrl) {
      this.closeCategoryMenu();
      const url = this.filterUrl(tag, taggedUrl);
      const nextTag = !tag || this.currentTag === tag ? "" : tag;
      if (nextTag === this.currentTag) return;

      try {
        await this.loadListing(url, { append: false });
      } catch (error) {
        if (error?.name === "AbortError") return;
        window.location.assign(publicUrl(url, this));
        return;
      }

      this.currentTag = nextTag;
      this.syncTags();
      this.syncUrl({ replace: false, url });
    }

    async loadMore(url) {
      if (this.loading) return;
      try {
        await this.loadListing(url, { append: true });
      } catch (error) {
        if (error?.name === "AbortError") return;
        window.location.assign(url);
      }
    }

    async handlePopState() {
      if (!this.blogUrl) return;
      const current = new URL(window.location.href);
      const blog = new URL(this.blogUrl, window.location.origin);
      if (
        current.pathname !== blog.pathname &&
        !current.pathname.startsWith(`${blog.pathname}/`)
      ) {
        return;
      }

      this.setView(this.readView());
      this.currentTag = this.tagFromPath(current.pathname);
      this.syncTags();

      try {
        await this.loadListing(current.href, { append: false });
      } catch (error) {
        if (error?.name === "AbortError") return;
        window.location.reload();
      }
    }

    syncUrl({ replace, url = window.location.href }) {
      const href = publicUrl(url, this);
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (href === current) return;
      if (replace) history.replaceState({}, "", href);
      else history.pushState({}, "", href);
    }

    async loadListing(url, { append }) {
      if (!this.sectionId) throw new Error("Missing section id");

      this.abort?.abort();
      const abort = new AbortController();
      this.abort = abort;
      this.loading = true;
      this.listing?.setAttribute("aria-busy", "true");

      try {
        const response = await fetch(sectionRequestUrl(url, this.sectionId), {
          signal: abort.signal,
        });
        if (!response.ok) throw new Error("Failed to load articles");
        this.applyListing(await response.text(), { append });
      } finally {
        if (this.abort === abort) {
          this.loading = false;
          this.abort = null;
          this.listing?.setAttribute("aria-busy", "false");
        }
      }
    }

    applyListing(html, { append }) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const next = doc.querySelector("[data-blog-listing]");
      if (!next || !this.listing) throw new Error("Failed to load articles");

      if (!append) {
        this.listing.replaceChildren(...next.childNodes);
        return;
      }

      const currentGrid = this.listing.querySelector('[data-layout="grid"]');
      const currentCompact = this.listing.querySelector('[data-layout="compact"]');
      const nextGrid = next.querySelector('[data-layout="grid"]');
      const nextCompact = next.querySelector('[data-layout="compact"]');

      if (!currentGrid || !nextGrid) {
        this.listing.replaceChildren(...next.childNodes);
        return;
      }

      currentGrid.append(...nextGrid.children);
      if (currentCompact && nextCompact) {
        currentCompact.append(...nextCompact.children);
      }

      this.listing.querySelector("[data-blog-more]")?.remove();
      const more = next.querySelector("[data-blog-more]");
      if (more) this.listing.append(more);
    }
  }

  if (!customElements.get("blog-index")) {
    customElements.define("blog-index", BlogIndex);
  }
})();
